import { Injectable, OnDestroy } from '@angular/core';
import { GeoJSON } from 'leaflet';
import { groupBy, isEqual, orderBy } from 'lodash';
import { FillPaint, Map as mapboxgl_Map } from 'mapbox-gl';
import {
    BehaviorSubject,
    Observable,
    Subject,
    TimeoutError,
    combineLatest,
    firstValueFrom,
    fromEvent,
    interval,
    merge,
} from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { IMapIMDFData, IMapIMDFStyles, IOrdinal } from 'shared';

const LAYER_BACKGROUND_FILL = 'imr-background-fill';
const SOURCE_BUILDINGS = 'imr-buildings';
const SOURCE_LEVELS = 'imr-levels';
const SOURCE_UNITS = 'imr-units';
const SOURCE_FIXTURES = 'imr-fixtures';

interface CachedSource {
    id: string;
    imdfOrdinalId?: string;
    category: string;
}

interface CachedLayer {
    id: string;
    imdfOrdinalId?: string;
}

@Injectable()
export class IndoorMapRenderer implements OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();
    private destroyed = false;
    private map: BehaviorSubject<mapboxgl_Map> = new BehaviorSubject<mapboxgl_Map | null>(null);
    private imdfData: BehaviorSubject<IMapIMDFData> = new BehaviorSubject<IMapIMDFData | null>(null);
    private imdfStyles: BehaviorSubject<IMapIMDFStyles> = new BehaviorSubject<IMapIMDFStyles | null>(null);
    private ordinals: BehaviorSubject<Array<IOrdinal>> = new BehaviorSubject<Array<IOrdinal> | null>(null);
    private ordinal: BehaviorSubject<IOrdinal> = new BehaviorSubject<IOrdinal | null>(null);
    private builtSourceCache: CachedSource[] = [];
    private builtLayerCache: CachedLayer[] = [];

    // Public Events

    // The render completion event allows listenrs to respond to the completion of the layer render process,
    // say to move other layers back to the top once the indoor map has been rendered.
    private _renderCompleted: Subject<void> = new Subject<void>();
    public renderCompleted: Observable<void> = this._renderCompleted.asObservable();

    public constructor() {
        // Completely rebuild sources and layers when the map or imdf data is updated
        combineLatest([this.map.pipe(distinctUntilChanged()), this.imdfData, this.imdfStyles])
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(10),
                filter(() => !this.destroyed),
                switchMap(async ([map, imdfData, imdfStyles]) => {
                    // As we don't want to rebuild the entire sources and layers when
                    // just the ordinal changes, we pass its value directly.
                    await this.initOrdinals(imdfData, this.ordinal.value);
                    await this.buildSources(map, imdfData);
                    await this.buildLayers(imdfData, imdfStyles, this.ordinal.value, map);
                })
            )
            .subscribe();

        // Update layer visibility when just the ordinal is updated
        combineLatest([this.map, this.ordinal.pipe(filter(Boolean))])
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(10),
                filter(() => !this.destroyed),
                switchMap(([map, ordinal]) => this.updateLayerVisibility(map, ordinal))
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.destroy$.next();
        this._renderCompleted.complete();
    }

    public setMap(map: mapboxgl_Map): IndoorMapRenderer {
        if (this.destroyed) throw new Error('Cannot set map on disposed renderer');
        this.map.next(map);
        return this;
    }

    public setIMDFData(data: IMapIMDFData): IndoorMapRenderer {
        if (this.destroyed) throw new Error('Cannot set IMDF data on disposed renderer');
        this.imdfData.next(data);
        return this;
    }

    public setIMDFStyles(styles: IMapIMDFStyles): IndoorMapRenderer {
        if (this.destroyed) throw new Error('Cannot set IMDF styles on disposed renderer');
        this.imdfStyles.next(styles);
        return this;
    }

    public setOrdinal(ordinal: IOrdinal): IndoorMapRenderer {
        if (this.destroyed) throw new Error('Cannot set ordinal on disposed renderer');
        this.ordinal.next(ordinal);
        return this;
    }

    public setOrdinalById(ordinalId: number): IndoorMapRenderer {
        if (this.destroyed) throw new Error('Cannot set ordinal on disposed renderer');
        const ordinal = (this.ordinals.value || []).find((o) => o.id === ordinalId);
        if (!ordinal) {
            console.warn("Attempted setting ordinal by id that doesn't exist");
            return;
        }
        this.ordinal.next(ordinal);
        return this;
    }

    private async initOrdinals(imdfData: IMapIMDFData, ordinal: IOrdinal): Promise<void> {
        // Parse the ordinals from the imdf data
        const ordinals = this.parseOrdinals(imdfData);
        this.ordinals.next(ordinals);
        // Ensure we have a correct ordinal
        if (ordinals.length && !ordinals.some((o) => isEqual(o, ordinal))) {
            this.setOrdinal(ordinals[0]);
        }
    }

    private async buildSources(map: mapboxgl_Map, imdfData: IMapIMDFData): Promise<void> {
        if (!map) return;
        // Wait for map to be loaded if needed
        await this.waitForMapLoad(map);
        // Remove pre-existing layers and sources
        this.builtLayerCache.forEach((layer) => {
            if (map.getLayer(layer.id)) map.removeLayer(layer.id);
        });
        this.builtLayerCache = [];
        this.builtSourceCache.forEach((source) => {
            if (map.getSource(source.id)) map.removeSource(source.id);
        });
        this.builtSourceCache = [];
        // Stop here if we don't have indoor map data
        if (!imdfData) return;
        // Add buildings source
        map.addSource(SOURCE_BUILDINGS, {
            type: 'geojson',
            data: imdfData.buildings,
        });
        this.builtSourceCache.push({ id: SOURCE_BUILDINGS, category: 'Building' } as CachedSource);
        // Define utility function for adding ordinal specific sources
        const addOrdinalBasedSources = (sourceName: string, featureCollection: GeoJSON.FeatureCollection): void => {
            // Group features per ordinal
            const featuresGroupedByOrdinal = groupBy(
                featureCollection.features,
                (feature: GeoJSON.Feature) => feature.properties.LEVEL_ID
            );
            Object.entries(featuresGroupedByOrdinal).forEach(([ordinalId, features]) => {
                // Per ordinal, group features per category
                const featuresGroupedByCategory = groupBy(
                    features,
                    (feature: GeoJSON.Feature) => feature.properties.CATEGORY
                );
                Object.entries(featuresGroupedByCategory).forEach(([category, features]) => {
                    // Determine the source id for this category
                    const sourceId = `${sourceName}-${ordinalId}-${category}`;
                    // Remove pre-existing source if needed
                    if (map.getSource(sourceId)) map.removeSource(sourceId);
                    // Add source
                    map.addSource(sourceId, {
                        type: 'geojson',
                        data: { type: 'FeatureCollection', features },
                    });
                    this.builtSourceCache.push({
                        id: sourceId,
                        imdfOrdinalId: ordinalId,
                        category,
                    } as CachedSource);
                });
            });
        };
        // Add sources for levels, units and fixtures
        addOrdinalBasedSources(SOURCE_LEVELS, imdfData.levels);
        addOrdinalBasedSources(SOURCE_UNITS, imdfData.units);
        addOrdinalBasedSources(SOURCE_FIXTURES, imdfData.fixtures);
    }

    private async waitForMapLoad(map: mapboxgl_Map): Promise<void> {
        if (!map.loaded()) {
            // The main load event does not always fire. That's why we'll poll the map's loaded states as well.
            try {
                await firstValueFrom(
                    merge([
                        combineLatest([
                            fromEvent(map, 'load'), // Standard load event
                            fromEvent(map, 'style.load'), // Style load event
                        ]),
                        interval(100).pipe(filter(() => map.loaded() && map.isStyleLoaded())), // Polling the loaded states
                    ]).pipe(take(1), timeout(5000))
                );
            } catch (e) {
                if (e instanceof TimeoutError) return;
                throw e;
            }
        }
    }

    private async updateLayerVisibility(map: mapboxgl_Map, ordinal: IOrdinal): Promise<void> {
        if (!map) return;
        // Wait for map to be loaded if needed
        await this.waitForMapLoad(map);
        // Update layer visibility
        this.builtLayerCache.forEach((layer) => {
            const shouldBeVisible = !layer.imdfOrdinalId || layer.imdfOrdinalId === ordinal.imdfId;
            map.setLayoutProperty(layer.id, 'visibility', shouldBeVisible ? 'visible' : 'none');
        });
    }

    private async buildLayers(
        imdfData: IMapIMDFData,
        imdfStyles: IMapIMDFStyles,
        ordinal: IOrdinal,
        map: mapboxgl_Map
    ): Promise<void> {
        if (!map) return;
        // Wait for map to be loaded if needed
        await this.waitForMapLoad(map);
        // Remove pre-existing layers
        this.builtLayerCache.forEach((layer) => {
            if (map.getLayer(layer.id)) map.removeLayer(layer.id);
        });
        this.builtLayerCache = [];
        // Determine if there is an outdoor map
        const outdoorMap = Boolean(map.getStyle()?.metadata?.['mapbox:origin']);
        // Add a background layer if we don't have an outdoor map
        if (!outdoorMap) {
            map.addLayer({
                id: LAYER_BACKGROUND_FILL,
                type: 'background',
                paint: {
                    'background-color': '#dddddd',
                },
            });
            this.builtLayerCache.push({ id: LAYER_BACKGROUND_FILL });
        }
        // Stop here if we don't have indoor map data
        if (!imdfData) return;
        // Add indoor layers
        this.builtSourceCache.forEach((source) => {
            // Get the layer paint
            const paint = this.buildPaint(source.category, imdfStyles);
            if (!paint) return;
            // Add the layer
            map.addLayer({
                id: source.id,
                type: 'fill',
                source: source.id,
                layout: {
                    visibility: !source.imdfOrdinalId || source.imdfOrdinalId === ordinal?.imdfId ? 'visible' : 'none',
                },
                paint,
            });
            this.builtLayerCache.push({
                id: source.id,
                imdfOrdinalId: source.imdfOrdinalId,
            });
        });
        // Inform listeners that the render process has completed
        this._renderCompleted.next();
    }

    private buildPaint(styleName: string, imdfStyles: IMapIMDFStyles): FillPaint {
        if (!imdfStyles) return null;
        const style = imdfStyles[styleName];
        if (!style) return null;
        const paint: mapboxgl.FillPaint = {};
        paint['fill-color'] = style.fill ? style.fillColor : '#000000';
        paint['fill-opacity'] = style.fill ? style.fillOpacity || 1.0 : 0.0;
        if (style.stroke) {
            paint['fill-outline-color'] = style.color;
        }
        return paint as FillPaint;
    }

    private parseOrdinals(data: IMapIMDFData): Array<IOrdinal> {
        if (!data)
            return [
                {
                    name: '',
                    id: 0,
                    imdfId: 'GROUND_FLOOR',
                },
            ];
        const ordinals = data.levels.features.map((f) => ({
            id: f.properties.ORDINAL,
            name: f.properties.NAME,
            imdfId: f.properties.LEVEL_ID,
        }));
        return orderBy(ordinals, ['id'], ['asc']);
    }
}
